<template>
    <!--
        The public-site search, rebuilt to match the admin dashboard's ⌘K palette
        (ui/src/components/layout/command-palette.vue). Deliberately not the shared
        modal.vue: that shell centres its card, pads it to md:p-8 and scrolls the whole
        card — a palette wants its rows edge to edge and its input pinned while results
        scroll.

        Teleported to <body> for the same reason modal.vue is (a route-level motion-blur
        filter creates a containing block that would otherwise trap `position: fixed`,
        HAP-1068). Because App.vue puts `.dark` on a div INSIDE #app rather than on <html>,
        a teleported subtree lands outside it and every `dark:` class would be dead
        (cfl_front_modal_dark_variants_are_dead). So the palette carries its own `.dark`
        wrapper on the teleported root — self-contained, no sitewide theme change.
    -->
    <Teleport to="body">
        <div :class="{ dark: darkMode.enabled }">
            <div
                @click="$emit('hide')"
                role="dialog"
                aria-modal="true"
                aria-label="Search"
                class="fixed inset-0 z-[10000] !m-0 h-screen w-full overscroll-none cursor-default
                       bg-gray-800/50 dark:bg-black/60 backdrop-blur-sm md:backdrop-blur-md
                       flex items-start justify-center px-3 pt-[10vh] sm:pt-[12vh]"
            >
                <div
                    @click.stop
                    class="w-full max-w-2xl overflow-hidden rounded-xl shadow-card
                           border border-gray-200 bg-white text-gray-800
                           dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                >
                    <!-- Search box -->
                    <div class="flex items-center gap-3 border-b border-gray-200 px-4 dark:border-white/10">
                        <i class="fal fa-search text-gray-400" />
                        <input
                            ref="input"
                            v-model="query"
                            type="text"
                            placeholder="Search sermons, resources, events, products…"
                            autocomplete="off"
                            spellcheck="false"
                            aria-label="Search"
                            class="w-full bg-transparent py-4 text-sm outline-none
                                   placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
                        />
                        <span
                            v-if="!query"
                            class="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold
                                   text-gray-400 dark:border-white/10 dark:text-gray-500"
                        >{{ shortcutHint }}</span>
                        <!-- Clears the text; on an empty box it closes, because people read an X as "close". -->
                        <button
                            @click="query ? (query = '') : $emit('hide')"
                            class="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            :aria-label="query ? 'Clear search' : 'Close search'"
                        ><i class="fal fa-times text-xs" /></button>
                    </div>

                    <!-- Results -->
                    <div ref="list" class="max-h-[55vh] overflow-y-auto py-2">
                        <div v-for="group in resultGroups" :key="group.key" class="mb-1">
                            <div class="flex items-center justify-between px-4 pb-1 pt-2">
                                <span class="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                                    {{ group.title }}
                                </span>
                                <span v-if="group.total > group.items.length" class="text-[10px] tabular-nums text-gray-400">
                                    {{ group.items.length }} of {{ group.total }}
                                </span>
                            </div>

                            <component
                                v-for="item in group.items"
                                :is="linkTag(item)"
                                :key="item.key"
                                v-bind="linkProps(item)"
                                :data-index="item.index"
                                @click="$emit('hide')"
                                @mousemove="selected = item.index"
                                class="flex w-full items-center gap-3 px-4 py-2 text-left"
                                :class="selected == item.index
                                    ? 'bg-ncfic-blue-50 dark:bg-white/10'
                                    : 'hover:bg-gray-50 dark:hover:bg-white/5'"
                            >
                                <!-- Thumbnail for content, icon chip for everything else -->
                                <span
                                    v-if="item.kind == 'content'"
                                    class="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg
                                           border border-white bg-gray-700 text-2xs uppercase text-white/70 dark:border-gray-600"
                                >
                                    <span v-if="item.label">{{ item.label[0] }}</span>
                                    <i v-else class="fal fa-file-alt" />
                                    <span
                                        v-if="item.image"
                                        class="absolute inset-0 h-full w-full bg-cover bg-center"
                                        :style="`background-image: url('${item.image}')`"
                                    />
                                </span>
                                <span
                                    v-else
                                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg
                                           bg-ncfic-blue-50 text-ncfic-blue-700 dark:bg-white/5 dark:text-ncfic-blue-200"
                                ><i :class="`fal fa-${item.icon} text-sm`" /></span>

                                <span class="min-w-0 flex-1">
                                    <span class="block truncate text-sm text-gray-800 dark:text-gray-200">
                                        {{ item.label }}
                                        <i v-if="item.href" class="fal fa-external-link ml-1 text-[8px] opacity-40" />
                                    </span>
                                    <span v-if="item.sublabel" class="block truncate text-[11px] text-gray-400">{{ item.sublabel }}</span>
                                </span>

                                <span class="shrink-0 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {{ item.tag }}
                                </span>
                            </component>
                        </div>

                        <div v-if="contentLoading" class="flex items-center justify-center py-4">
                            <Spinner :small="true" class="h-6 w-6" />
                        </div>

                        <!-- Empty is a state. -->
                        <div v-else-if="!flatResults.length" class="px-4 py-10 text-center text-xs text-gray-400">
                            <template v-if="trimmedQuery">Nothing matches “{{ trimmedQuery }}”.</template>
                            <template v-else>Start typing to search across all content.</template>
                        </div>
                    </div>

                    <!--
                        Renders nothing; it only fetches. Mounted on the settled query so a
                        burst of keystrokes costs one request.
                    -->
                    <Mango
                        v-if="debouncedQuery"
                        :algoliaSearch="debouncedQuery"
                        :algoliaFilters="contentFilters"
                        :globalSearch="true"
                        :debounce="0"
                        :query="{ limit: 30, optionalFilters: cflFirst }"
                        @update:data="contentResults = $event || []"
                        @update:totalResults="contentTotal = $event"
                        @update:loading="contentLoading = $event"
                    />

                    <!-- Keyboard legend -->
                    <div
                        class="flex items-center gap-4 border-t border-gray-200 px-4 py-2 text-[10px] text-gray-400
                               dark:border-white/10 dark:text-gray-500"
                    >
                        <span><i class="fal fa-arrow-up" /><i class="fal fa-arrow-down ml-1" /> navigate</span>
                        <span><i class="fal fa-level-down-alt fa-rotate-90" /> {{ selected < 0 && trimmedQuery ? 'see all results' : 'open' }}</span>
                        <span>esc close</span>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
/*
    Public-site search palette (HAP-4122).

    Replaces the heavy "Search for Anything!" modal that used to live inside nav.vue with
    a command-palette layout that mirrors the admin dashboard's ⌘K. The old box only ever
    showed a flat list of big content cards; this groups the two things people reach for —
    a page they are trying to get to, then content — and adds full keyboard navigation.

    Pages filter synchronously off an in-memory list. Only the content group costs a
    network request (Algolia, via <Mango>), so only it is debounced.
*/

// Enough rows per group that the answer is nearly always visible, few enough that arrowing
// from the top of Pages to the top of Content is not a journey.
const PER_GROUP = 8

// The public collections the site indexes in Algolia. Mirrors pages/search.vue.
const CONTENT_COLLECTIONS = ['events', 'resources', 'sermons', 'podcastEpisodes', 'products', 'people']

/*
    Everything CFL publishes outranks every network-church sermon (David, HAP-4825).
    The index ranks its `filters` criterion first, so every record matching this
    optional filter (anything not a sermon) sorts above every sermon, whatever the text
    relevance. Queries that pass no optional filters tie on that criterion and rank
    exactly as before. One negative filter, because the Algolia plan rejects a query
    with several optional filters (402 "number of optionalFilters is too high").
*/
const CFL_FIRST = ['collection:-sermons']

// Primary destinations, reachable by keyword the same way the admin palette reaches tools.
const PAGES = [
    { id: 'about', label: 'About', sublabel: 'Who we are', icon: 'info-circle', to: '/about' },
    { id: 'network', label: 'Church Network', sublabel: 'Find a church', icon: 'project-diagram', to: '/network/index' },
    { id: 'events', label: 'Events', sublabel: 'Conferences & gatherings', icon: 'calendar-alt', to: '/events' },
    { id: 'podcasts', label: 'Podcasts', sublabel: 'Listen to our shows', icon: 'podcast', to: '/podcasts' },
    { id: 'resources', label: 'Resources', sublabel: 'Articles, audio & video', icon: 'book-open', to: '/resources' },
    { id: 'products', label: 'Store', sublabel: 'Books & media', icon: 'store', to: '/products' },
    { id: 'donate', label: 'Donate', sublabel: 'Support the ministry', icon: 'hand-holding-heart', to: '/donate' },
    { id: 'account', label: 'My Account', sublabel: 'Orders & profile', icon: 'user-circle', to: '/account' },
]

// The Bible-reading-plan card the old nav surfaced for reading-related queries.
const READING_PLAN_TERMS = ['read', 'journey', 'jttb', 'yearly', 'plan', 'reading', 'bible']

const COLLECTION_LABELS = {
    events: 'Event',
    resources: 'Resource',
    sermons: 'Sermon',
    podcastEpisodes: 'Podcast',
    products: 'Product',
    people: 'Speaker',
    blogs: 'Article',
}

function routeForEntry(entry) {
    if (entry.collection == 'people') return `/resources/authors/${entry.id}`
    if (entry.collection == 'podcastEpisodes') {
        const podcastId = entry.podcast?.id || entry.podcast?.[0]?.id || 'church-and-family-life-podcast'
        return `/podcasts/${podcastId}/${entry.id}`
    }
    return `/${entry.collection}/${entry.id}`
}

export default {
    inject: ['store', 'darkMode'],
    emits: ['hide'],
    data() {
        return {
            query: '',
            // -1 = nothing highlighted, so Enter goes to the full results page rather
            // than whichever row happens to be first (HAP-4825).
            selected: -1,
            // The settled query the content search actually runs on.
            debouncedQuery: '',
            contentResults: [],
            // Algolia's nbHits; the 30 fetched rows are only the head of it.
            contentTotal: null,
            contentLoading: false,
            contentTimer: null,
            keyListener: null,
            bodyOverflow: '',
        }
    },
    mounted() {
        this.keyListener = this.onKeydown.bind(this)
        document.addEventListener('keydown', this.keyListener)
        this.lockBody()
        this.$nextTick(() => this.$refs.input?.focus())
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.keyListener)
        clearTimeout(this.contentTimer)
        this.unlockBody()
    },
    watch: {
        query(value) {
            // A new query means the old highlight points at a row that no longer exists.
            this.selected = -1
            clearTimeout(this.contentTimer)
            const q = value.trim()
            if (!q) {
                this.debouncedQuery = ''
                this.contentResults = []
                this.contentTotal = null
                this.contentLoading = false
                return
            }
            // Show the spinner from the first keystroke, not from when the request leaves.
            this.contentLoading = true
            this.contentTimer = setTimeout(() => { this.debouncedQuery = q }, 200)
        },
        selected() {
            this.$nextTick(() => {
                this.$refs.list
                    ?.querySelector(`[data-index="${this.selected}"]`)
                    ?.scrollIntoView({ block: 'nearest' })
            })
        },
        flatResults() {
            // Results can shrink under the highlight while a debounced search lands.
            if (this.selected > this.flatResults.length - 1) this.selected = -1
        },
        // Belt and braces: whatever navigated, the palette does not outlive the page it was opened on.
        $route() {
            this.$emit('hide')
        },
    },
    computed: {
        isMac() {
            return typeof navigator != 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
        },
        shortcutHint() {
            return this.isMac ? '⌘K' : 'Ctrl K'
        },
        trimmedQuery() {
            return this.query.trim()
        },
        cflFirst() {
            return CFL_FIRST
        },
        contentFilters() {
            return CONTENT_COLLECTIONS.map((c) => `collection:${c}`).join(' OR ')
        },
        matchedPages() {
            const q = this.trimmedQuery.toLowerCase()
            return PAGES.filter((p) => `${p.label} ${p.sublabel}`.toLowerCase().includes(q))
        },
        showReadingPlan() {
            const q = this.trimmedQuery.toLowerCase()
            return !!q && READING_PLAN_TERMS.some((t) => q.includes(t))
        },
        // Past events are dead links; the old nav dropped them and so do we.
        visibleContent() {
            const now = new Date()
            return this.contentResults.filter((r) => r.collection != 'events' || new Date(r.startDate) > now)
        },
        /*
            The rendered groups, each row already carrying the index it occupies in the single
            flat list the arrow keys walk. Building it here rather than in the template is what
            lets ArrowDown cross a group boundary without the template knowing about it.
        */
        resultGroups() {
            const groups = []
            const push = (key, title, all, toItem, total = all.length) => {
                if (!all.length) return
                groups.push({ key, title, total: Math.max(total || 0, all.length), items: all.slice(0, PER_GROUP).map(toItem) })
            }

            if (!this.trimmedQuery) {
                push('jump', 'Jump to', PAGES, this.pageItem)
            } else {
                const pages = this.matchedPages.map(this.pageItem)
                if (this.showReadingPlan) pages.unshift(this.readingPlanItem())
                push('pages', 'Pages', pages, (x) => x)
                // CFL_FIRST puts every CFL match ahead of network sermons, so sermons only fill what is left.
                push('content', 'Content', this.visibleContent, this.contentItem, this.contentTotal)
                // A guaranteed way to the full results page, always last so Enter can reach it.
                groups.push({ key: 'all', title: 'Search', total: 1, items: [this.allResultsItem()] })
            }

            let index = 0
            groups.forEach((g) => g.items.forEach((item) => { item.index = index++ }))
            return groups
        },
        flatResults() {
            return this.resultGroups.flatMap((g) => g.items)
        },
    },
    methods: {
        pageItem(page) {
            return {
                key: `page:${page.id}`,
                kind: 'page',
                label: page.label,
                sublabel: page.sublabel,
                tag: 'Page',
                icon: page.icon,
                to: page.to,
            }
        },
        readingPlanItem() {
            return {
                key: 'page:reading-plan',
                kind: 'page',
                label: 'Journey Through the Bible',
                sublabel: 'Daily Bible reading plan',
                tag: 'External',
                icon: 'book',
                href: 'https://read.jttb.co',
            }
        },
        contentItem(entry) {
            return {
                key: `content:${entry.collection}:${entry.id}`,
                kind: 'content',
                label: entry.title || 'Untitled',
                sublabel: (entry.authors || entry.speakers || []).map((a) => a?.title).filter(Boolean).join(', ') || null,
                tag: COLLECTION_LABELS[entry.collection] || entry.collection,
                image: entry.image?.url || entry.bannerImage?.url || entry.video?.thumbnail,
                to: routeForEntry(entry),
            }
        },
        allResultsItem() {
            return {
                key: 'all-results',
                kind: 'page',
                label: `See all results for “${this.trimmedQuery}”`,
                sublabel: this.contentTotal ? `${this.contentTotal} result${this.contentTotal == 1 ? '' : 's'}` : null,
                tag: 'All',
                icon: 'search',
                to: `/search?q=${encodeURIComponent(this.trimmedQuery)}`,
            }
        },
        linkTag(item) {
            return item.href ? 'a' : 'router-link'
        },
        linkProps(item) {
            return item.href ? { href: item.href, target: '_blank', rel: 'noopener' } : { to: item.to }
        },
        onKeydown(e) {
            if (e.key == 'Escape') {
                e.preventDefault()
                return this.$emit('hide')
            }
            if (e.key == 'ArrowDown') {
                e.preventDefault()
                return this.move(1)
            }
            if (e.key == 'ArrowUp') {
                e.preventDefault()
                return this.move(-1)
            }
            if (e.key == 'Enter') {
                const item = this.selected < 0 ? (this.trimmedQuery ? this.allResultsItem() : null) : this.flatResults[this.selected]
                if (!item) return
                e.preventDefault()
                this.activate(item)
            }
        },
        move(step) {
            const count = this.flatResults.length
            if (!count) return
            // Wrap, so holding ArrowUp from the top row lands on the last result.
            if (this.selected < 0) this.selected = step > 0 ? 0 : count - 1
            else this.selected = (this.selected + step + count) % count
        },
        activate(item) {
            // Close first: on a cold visit the destination chunk lazy-loads inside
            // Suspense, and the palette should not wait on that.
            this.$emit('hide')
            if (item.href) window.open(item.href, '_blank', 'noopener')
            // Hitting Enter on the page you are already on is a no-op, not an unhandled rejection.
            else this.$router.push(item.to).catch(() => {})
        },
        lockBody() {
            this.bodyOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'
        },
        unlockBody() {
            // Never hand back a lock the palette found already stuck on.
            document.body.style.overflow = this.bodyOverflow == 'hidden' ? '' : this.bodyOverflow
        },
    },
}
</script>
